import React from 'react';
import * as log from 'app/log';
import classNames from 'classnames';

type Props = {
  title?: string;
  description?: string;
  bodyClassName?: string;
  loadRecaptcha?: boolean;
  ldJson?: Record<string, string | number | boolean>;
  metaTags?: Record<string, string>;
};

/**
 * Includes the absolutely minimum metadata that should be included on every
 * page. All pages should export a Head with this.
 */
const HeadContent = (props: Props) => {
  const {
    title = 'QLab',
    description = 'QLab is sound, video, and lighting control for macOS. It’s used by everyone, big and small.',
    bodyClassName,
    loadRecaptcha = true,
    ldJson,
    metaTags,
  } = props;

  const BUILD_ENV = ENV.BUILD_ENV;
  const enforce_required_env =
    BUILD_ENV === 'production' || BUILD_ENV === 'staging';
  let PAYPAL_CLIENT_ID: string | undefined | null = ENV.PAYPAL_CLIENT_ID;
  let PAYPAL_CURRENCY_CODE: string | undefined | null =
    ENV.PAYPAL_CURRENCY_CODE;
  let RECAPTCHA_SITE_KEY: string | undefined | null = ENV.RECAPTCHA_SITE_KEY;

  // for deployments, throw an error if we're missing required configuration
  if (enforce_required_env) {
    if (PAYPAL_CLIENT_ID == null) {
      throw new Error('missing required env ENV: PAYPAL_CLIENT_ID');
    }
    if (PAYPAL_CURRENCY_CODE == null) {
      throw new Error('missing required env ENV: PAYPAL_CURRENCY_CODE');
    }
    if (RECAPTCHA_SITE_KEY == null) {
      throw new Error('missing required env ENV: RECAPTCHA_SITE_KEY');
    }
  }

  // in non-deployed environments, log a warning and set an empty string
  if (PAYPAL_CLIENT_ID == null) {
    log.info('missing env definition for PAYPAL_CLIENT_ID');
    PAYPAL_CLIENT_ID = 'undefined';
  }
  if (PAYPAL_CURRENCY_CODE == null) {
    log.info('missing env definition for PAYPAL_CURRENCY_CODE');
    PAYPAL_CURRENCY_CODE = 'USD';
  }
  if (RECAPTCHA_SITE_KEY == null) {
    log.info('missing env definition for RECAPTCHA_SITE_KEY');
    RECAPTCHA_SITE_KEY = 'undefined';
  }

  const allMetaTags = {
    'twitter:card': 'summary_large_image',
    'twitter:site': '@QLabApp',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': 'https://qlab.app/qlab-og-image.png',
    ...metaTags,
  };

  return (
    <>
      <title>{title}</title>
      <html lang="en" />
      <meta charSet="utf-8" />
      <body className={classNames(bodyClassName)} />

      <meta name="description" content={description} />
      <meta
        name="keywords"
        content="QLab Figure 53 theater audio video lighting software cues control macos"
      />
      <link href="/favicon.ico" rel="shortcut icon" />

      {loadRecaptcha && (
        <script
          src={`https://www.recaptcha.net/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`}
        />
      )}

      {ldJson && (
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      )}

      <meta content={title} property="og:title" />
      <meta content={description} property="og:description" />
      <meta content={description} property="description" />

      {Object.entries(allMetaTags).map(([name, content]) => (
        <meta key={name} name={name} content={content} />
      ))}
    </>
  );
};

export default HeadContent;
